// DOM helpers for the HUD and the flow screens. HUD updates are change-driven (docs/ARCHITECTURE.md §8): the slots
// below write to the DOM only when the shown value actually changes, so a 20 Hz private-state stream costs nothing
// while the numbers hold still.

import type { WebGLRenderer } from 'three'
import { HudError } from './errors'

const PERCENT = 100

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag)
  element.className = className
  if (text !== undefined) element.textContent = text
  return element
}

export function button(className: string, text: string): HTMLButtonElement {
  const element = el('button', className, text)
  element.type = 'button'
  return element
}

/** An element's text, written only when it changes. */
export class TextSlot {
  private value: string | null = null

  constructor(readonly element: HTMLElement) {}

  set(text: string): void {
    if (text === this.value) return
    this.value = text
    this.element.textContent = text
  }
}

/** A CSS custom property holding a 0..1 fraction as a percentage, written only when the whole percentage changes. */
export class FractionSlot {
  private percent = Number.NaN

  constructor(
    private readonly element: HTMLElement,
    private readonly property: string,
  ) {}

  set(fraction: number): void {
    const percent = Math.round(Math.min(1, Math.max(0, fraction)) * PERCENT)
    if (percent === this.percent) return
    this.percent = percent
    this.element.style.setProperty(this.property, `${percent}%`)
  }
}

/** One-shot timers released when `signal` aborts (screen or HUD lifetime). */
export class Timers {
  private readonly handles = new Set<number>()

  constructor(signal: AbortSignal) {
    if (signal.aborted) throw new HudError('timers bound to an already-aborted signal')
    signal.addEventListener('abort', this.clearAll, { once: true })
  }

  after(delayMs: number, callback: () => void): number {
    const handle = window.setTimeout(() => {
      this.handles.delete(handle)
      callback()
    }, delayMs)
    this.handles.add(handle)
    return handle
  }

  cancel(handle: number): void {
    window.clearTimeout(handle)
    this.handles.delete(handle)
  }

  private readonly clearAll = (): void => {
    for (const handle of this.handles) window.clearTimeout(handle)
    this.handles.clear()
  }
}

/** The element hosting the page's single canvas: flow screens mount their DOM overlays there. */
export function canvasHost(renderer: WebGLRenderer): HTMLElement {
  const host = renderer.domElement.parentElement
  if (host === null) throw new HudError('the renderer canvas is not attached to a host element')
  return host
}

/** True when a key event is typed into a text field, so global hotkeys must leave it alone. */
export function isTextEntry(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable)
}
